/**
 * Combining employment income with cash savings (ADR-0002). The 1.7
 * guidance permits Category A income to combine with Category D savings;
 * savings can NEVER combine with Category B part 2 (the 12-month limb) or
 * with F/G (register §4, "Savings combinability").
 *
 * - A+D: required savings = floor + multiplier × (threshold − income).
 *   Income at or above the threshold means the requirement is met on income
 *   alone and no savings are asked for.
 * - B+D: the part-2 restriction plus the thin fact set means this cannot be
 *   assessed here — PENDING_EVIDENCE with the position explained, never a
 *   guess. Full Category B combination arrives with workstream F2.
 */
import { categorise } from "./financial_category.js";
import type { EvaluatePredicate } from "./predicate.js";
import { savingsSufficiency } from "./savings_formula.js";

export const combine_income_savings: EvaluatePredicate = (facts, rule, ctx) => {
  const thresholdResult = ctx.results.get("FIN.THRESHOLD");
  if (!thresholdResult || thresholdResult.status !== "MET") {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.THRESHOLD", "FIN.CATEGORY"],
      notes: ["The requirement cannot be calculated until the applicable income threshold is determined."],
    };
  }
  const threshold = thresholdResult.figures?.["threshold"];
  if (threshold === undefined) throw new Error("FIN.THRESHOLD result carries no threshold figure");

  const tenureMonths = numeric(ctx.paramFigure(rule, "tenure_months_figure"));
  const category = categorise(facts, tenureMonths);

  if (category === null) {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.CATEGORY"],
      notes: [
        "How long the sponsor has been with their current employer is needed before income and savings can be combined.",
      ],
    };
  }

  if (category === "B+D") {
    return {
      status: "PENDING_EVIDENCE",
      value: "B+D",
      because: ["FIN.THRESHOLD", "FIN.CATEGORY"],
      notes: [
        "With less than 6 months in the current employment, Category B applies, and the rules do not allow cash savings to be combined with Category B's 12-month income history. Assessing this combination needs more information than this calculation collects.",
      ],
    };
  }

  // A+D from here.
  const income = facts.finances.sponsor_employment?.gross_annual ?? null;
  if (income === null) {
    return {
      status: "PENDING_EVIDENCE",
      because: ["FIN.THRESHOLD", "FIN.CATEGORY"],
      notes: ["The gross annual salary is needed."],
    };
  }

  if (income >= threshold) {
    return {
      status: "MET",
      value: "A+D",
      figures: { threshold, income, shortfall: 0, required_savings: 0 },
      because: ["FIN.THRESHOLD", "FIN.CATEGORY"],
      notes: ["The gross annual salary meets the applicable figure on its own; no savings are asked for."],
    };
  }

  const outcome = savingsSufficiency({
    facts,
    threshold,
    income,
    floor: numeric(ctx.paramFigure(rule, "floor_figure")),
    multiplier: numeric(ctx.paramFigure(rule, "multiplier_figure")),
    holdingMonths: numeric(ctx.paramFigure(rule, "holding_months_figure")),
    extraFigures: { income },
  });
  outcome.value = "A+D";
  outcome.because = [...new Set([...(outcome.because ?? []), "FIN.CATEGORY"])];
  return outcome;
};

function numeric(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}
